class PreOrderController extends HTMLElement {
  constructor() {
    super()

    this.variantHasBeenSelected = !!new URLSearchParams(document.location.search).get("variant")
    this.cartState = {}

    this.setInitialCartState()
    this.setInitialUiState()
    this.setupListeners()
  }

  setupListeners() {
    window.addEventListener('cart:Refreshed', this.handlePushCartChanged.bind(this))
    window.addEventListener('variant:Changed', this.handleVariantChanged.bind(this))
    window.addEventListener('quickSearch:cardsRendered', this.handleQuickSearchCardsRendered.bind(this))
    window.addEventListener('recsTabs:rendered', this.handleProductRecsRendered.bind(this))
    window.addEventListener('pushCart:recsRendered', this.handlePushCartRecsRendered.bind(this))
  }

  setInitialUiState() {
    this.setMainFormState()

    if (this.pushCartRecsItems.length) {
      this.pushCartRecsItems.forEach(recsItem => {
        this.setPushCartRecsItemState(recsItem)
      })
    }

    if(this.productCardEls.length) {
      this.productCardEls.forEach(cardEl => {
        this.setProductCardUi(cardEl)
      })
    }

    if(this.hotspotEls.length) {
      this.hotspotEls.forEach(hotspotEl => {
        this.setHotspotUi(hotspotEl)
      })
    }

    if(this.quickAddOverlayItems.length) {
      this.quickAddOverlayItems.forEach(overlayItem => {
        this.setQuickAddOverlayUi(overlayItem)
      })
    }
  }

  setInitialCartState() {
    const cartHasPreOrderItems = this.getAttribute('data-cart-has-pre-order-items') === 'true'

    this.cartState = {
      isEmpty: this.getAttribute('data-cart-has-items') === 'false',
      hasPreOrderItems: cartHasPreOrderItems,
      preOrderProductId: this.getAttribute('data-pre-order-product-id'),
      hasRegularItems: this.getAttribute('data-cart-has-items') === 'true' && !cartHasPreOrderItems
    }
  }

  setInitialQuickAddState() {
    const quickAddButton = document.querySelector(
      `[data-pre-order-controller-external-el="quick-add-button-${this.currentProductId}"]`
    )

    if (!quickAddButton) return

    const shouldEnable = !hasConflict

    quickAddButton.setAttribute('aria-hidden', !shouldEnable)

    if (shouldEnable) {
      quickAddButton.removeAttribute('disabled')
      quickAddButton.removeAttribute('aria-disabled')
    } else {
      quickAddButton.disabled = true
      quickAddButton.setAttribute('aria-disabled', 'true')
    }
  }

  setMainFormState() {
    if (!this.variantHasBeenSelected) return

    this.setMainFormAtcState()
    this.setMainFormVariantRadiosState()
    this.setMainFormWarningTextState()
  }

  setMainFormAtcState() {
    if (!this.addToCartCta) return

    const hasConflict = this.getHasConflict(this.addToCartCta)

    if (!hasConflict) {
      this.addToCartCta.style.display = 'block'
      this.addToCartCta.disabled = !this.variantHasBeenSelected
      this.addToCartCta.removeAttribute('aria-hidden')
    } else {
      this.addToCartCta.style.display = 'none'
      this.addToCartCta.setAttribute('aria-hidden', 'true')
    }
  }

  setMainFormVariantRadiosState() {
    if (!this.variantRadiosWrapper) return

    const hasConflict = this.getHasConflict(this.variantRadiosWrapper)
    this.variantRadiosWrapper?.setAttribute('data-pre-order-conflict', hasConflict)
  }

  setMainFormWarningTextState() {
    if (!this.warningTextEl) return

    const hasConflict = this.getHasConflict(this.warningTextEl)
    this.warningTextEl.setAttribute('aria-hidden', !hasConflict)
  }

  getHasConflict(el) {
    const elIsPreOrderItem = el.getAttribute('data-current-item-is-pre-order') === 'true'
    const elProductId = parseInt(el.getAttribute('data-product-id'))

    if (this.cartState.isEmpty) {
      return false
    }

    if (this.cartState.hasPreOrderItems) {
      if (elIsPreOrderItem) {
        this.cartState.preOrderProductId

        return this.cartState.preOrderProductId.toString() !== elProductId.toString()
      } else {
        return true
      }
    }

    if (this.cartState.hasRegularItems) {
      return elIsPreOrderItem
    }

    return false
  }

  handlePushCartChanged(event) {
    const { cart } = event.detail

    this.updateCartState(cart)
  }

  handleVariantChanged() {
    this.variantHasBeenSelected = true
    this.setMainFormState()
  }

  updateUiBasedOnCartState() {
    this.updatePushCartMessageUi()
    this.setMainFormState()

    if (this.pushCartRecsItems.length) {
      this.pushCartRecsItems.forEach(recsItem => {
        this.setPushCartRecsItemState(recsItem)
      })
    }

    if(this.productCardEls.length) {
      this.productCardEls.forEach(cardEl => {
        this.setProductCardUi(cardEl)
      })
    }

    if(this.hotspotEls.length) {
      this.hotspotEls.forEach(hotspotEl => {
        this.setHotspotUi(hotspotEl)
      })
    }

    if(this.quickAddOverlayItems.length) {
      this.quickAddOverlayItems.forEach(overlayItem => {
        this.setQuickAddOverlayUi(overlayItem)
      })
    }
  }

  updateCartState(cart) {
    const preOrderItems = cart.items.filter(item =>
      item.properties && item.properties['_pre_order_item'] === 'true'
    )

    this.cartState = {
      isEmpty: cart.items.length === 0,
      hasPreOrderItems: preOrderItems.length > 0,
      preOrderProductId: preOrderItems.length > 0 ? parseInt(preOrderItems[0].product_id) : null,
      hasRegularItems: cart.items.length > 0 && preOrderItems.length === 0
    }

    this.updateUiBasedOnCartState()
  }

  setProductCardUi(productCardEl) {
    const hasConflict = this.getHasConflict(productCardEl)
    productCardEl.setAttribute('data-has-pre-order-conflict', hasConflict)
  }

  setHotspotUi(hotspotEl) {
    const hasConflict = this.getHasConflict(hotspotEl)
    hotspotEl.setAttribute('data-has-pre-order-conflict', hasConflict)
  }

  setQuickAddOverlayUi(quickAddOverlayEl) {
    const hasConflict = this.getHasConflict(quickAddOverlayEl)
    quickAddOverlayEl.setAttribute('data-has-pre-order-conflict', hasConflict)
  }

  setPushCartRecsItemState(recsItem) {
    const hasConflict = this.getHasConflict(recsItem)
    recsItem.setAttribute('aria-hidden', hasConflict)
  }

  handleQuickSearchCardsRendered() {
    if (!this.quickSearchResultsList) return

    const productCardEls = this.quickSearchResultsList.querySelectorAll('[data-pre-order-controller-external-el="product-card"]')

    if (!productCardEls.length) return

    productCardEls.forEach(cardEl => {
      this.setProductCardUi(cardEl)
    });
  }

  handleProductRecsRendered()  {
    if (!this.klevuRecsCarousel) return

    const productCardEls = this.klevuRecsCarousel.querySelectorAll('[data-pre-order-controller-external-el="product-card"]')

    if (!productCardEls.length) return

    productCardEls.forEach(cardEl => {
      this.setProductCardUi(cardEl)
    });
  }

  handlePushCartRecsRendered()  {
    const productCardEls = document.querySelectorAll('[data-pre-order-controller-external-el="push-cart-recs-quick-add"]')

    if (!productCardEls.length) return

    productCardEls.forEach(cardEl => {
      this.setPushCartRecsItemState(cardEl)
    });
  }

  updatePushCartMessageUi () {
    const pushCartMessageEl = document.querySelector(
      '[data-pre-order-controller-external-el="cart-message"]'
    )

    if (!pushCartMessageEl) return

    pushCartMessageEl.setAttribute('aria-hidden', !this.cartState.hasPreOrderItems)
  }

  get addToCartCta() {
    return document.querySelector(
      '[data-pre-order-controller-external-el="main-product-form-add-to-cart"]'
    )
  }

  get variantRadios() {
    return document.querySelectorAll(
      '[data-pre-order-controller-external-el="main-product-form-variant-input"]'
    )
  }

  get warningTextEl() {
    return document.querySelector(
      '[data-pre-order-controller-external-el="main-product-form-pre-order-warning"]'
    )
  }

  get variantRadiosWrapper() {
    const radios = this.variantRadios
    return radios.length ? radios[0].closest(
      '[data-pre-order-controller-external-el="main-product-form-variant-input-group"]'
    ) : null
  }

  get pushCartRecsItems() {
    return document.querySelectorAll('[data-pre-order-controller-external-el="push-cart-recs-quick-add"]')
  }

  get productCardEls() {
    return document.querySelectorAll('[data-pre-order-controller-external-el="product-card"]')
  }

  get hotspotEls() {
    return document.querySelectorAll('[data-pre-order-controller-external-el="hotspot"]')
  }

  get quickAddOverlayItems() {
    return document.querySelectorAll(
      `[data-pre-order-controller-external-el="quick-add-overlay-item"]`
    )
  }

  get quickSearchResultsList() {
    return document.querySelector('[data-search-overlay-el="resultsList"]')
  }

  get klevuRecsCarousel() {
    return document.querySelector('[data-pre-order-controller-external-el="klevu-tabs-recs-carousel"]')
  }
}

customElements.define('pre-order-controller', PreOrderController)
